import React, { ReactNode } from 'react';
import { ERROR_500 } from 'routes/paths';
import history from 'shared/services/history';
import store from 'redux/store';
import i18n from 'services/i18n';
import { getCustomerSupportError, getCustomerSupportErrorPart } from 'utilities/global';
import { HOSTED_PAGE_TYPES, ERROR_500_MESSAGES, ERROR_PAGE_TYPES } from 'consts/global';
import { TFunctionKeys } from 'i18next';
import { DigitalWalletProvider } from 'consts';
import { getPaymentErrorPrefix, getPayoutErrorPrefix } from 'utilities/customError';
import localStorageService from 'services/localStorage';
import { getRouteWithToken } from 'utilities';

export const redirectToError = ({
  url,
  error,
  tokenization = false,
  expiredUrl,
  errorOrigin,
  ...rest
}: {
  url?: string;
  error?: Record<string, any>;
  tokenization?: boolean;
  expiredUrl?: string;
  errorOrigin?: string;
  [key: string]: any;
}) => {
  const { global } = store.getState();
  if (global.isSdk) return;

  const errorMessage = error?.response?.data?.status?.error_code;

  switch (errorMessage) {
    case ERROR_500_MESSAGES.PAGE_EXPIRED:
      return redirectToExpired({ tokenization, expiredUrl, ...rest });
    case ERROR_500_MESSAGES.PAGE_DECLINED:
      return redirectToDeclined({ tokenization, ...rest });
    case ERROR_500_MESSAGES.INVALID_PHONE_NUMBER: {
      const fromOtp = errorOrigin === HOSTED_PAGE_TYPES.otp;
      return history.push(ERROR_500, {
        fromOtp,
        phoneNumberInvalid: true,
        type: ERROR_PAGE_TYPES.ERROR_500,
        ...rest
      });
    }
    default: {
      if (url) {
        history.push(url);
      } else {
        history.push(ERROR_500, {
          type: ERROR_PAGE_TYPES.ERROR_500,
          ...rest
        });
      }
    }
  }
};

export const redirectToExpired = (params?: {
  tokenization?: boolean;
  expiredUrl?: string;
  [key: string]: any;
}) => {
  const { expiredUrl, tokenization, ...rest } = params || {};
  const { global } = store.getState();
  if (global.isSdk) return;

  const { pageToken, merchant } = global || {};
  const { website, color, customerSupport } = merchant || {};
  localStorageService.setItem(
    `${pageToken}_merchant_data`,
    JSON.stringify({ website, color, customerSupport })
  );

  const urlToRedirect = expiredUrl || ERROR_500;
  history.push(getRouteWithToken(urlToRedirect, pageToken), {
    expired: true,
    tokenization,
    type: ERROR_PAGE_TYPES.PAGE_EXPIRED,
    ...rest
  });
};

export const redirectToDeclined = ({
  tokenization,
  ...rest
}: {
  tokenization?: boolean;
  [key: string]: any;
}) => {
  const { global } = store.getState();
  if (global.isSdk) return;
  history.push(ERROR_500, {
    declined: true,
    tokenization,
    type: ERROR_PAGE_TYPES.PAGE_DECLINED,
    ...rest
  });
};

export const getDefaultErrorMessage = ({
  customKey,
  openingSentence,
  customerSupportPart = getCustomerSupportError()
}: {
  customKey?: TFunctionKeys | TFunctionKeys[];
  openingSentence?: string;
  customerSupportPart?: ReactNode;
}) => {
  let translatedCustomKey = null;
  if (customKey) {
    translatedCustomKey = i18n.t(customKey);
  }
  const prefix = openingSentence || translatedCustomKey;

  return (
    <>
      {prefix} <br /> {customerSupportPart}
    </>
  );
};

export const getInvalidStatusErrorMessage = () => {
  const prefix = i18n.t('errors.invalid_status');
  const customerSupportPrefix = i18n.t('errors.customer_support');
  const customerSupportBody = getCustomerSupportErrorPart();
  const customerSupportPart = customerSupportBody?.length ? (
    <>
      <br />
      {customerSupportPrefix} <br />
      {customerSupportBody}
    </>
  ) : null;

  return (
    <>
      {prefix} {customerSupportPart}
    </>
  );
};

export const getCreatePaymentErrorMessage = ({
  isCard,
  digitalWalletProvider,
  isDefault
}: {
  isCard?: boolean;
  digitalWalletProvider?: DigitalWalletProvider;
  isDefault?: boolean;
}) => {
  const openingSentence = getPaymentErrorPrefix({
    isCard,
    digitalWalletProvider,
    isDefault
  });
  return getDefaultErrorMessage({ openingSentence });
};

export const getPayoutRequiredFieldsErrorMessage = () => {
  const openingSentence = getPayoutErrorPrefix();
  return getDefaultErrorMessage({ openingSentence });
};

export const getActivateIssuedCardErrorMessage = ({ suffix = '' }) => {
  const openingSentence = i18n.t('activate_issued_card.error');
  const getPrefixFunction = ({
    hasCustomerSupportDetails
  }: {
    hasCustomerSupportDetails?: boolean;
  }) => {
    const prefix = `${i18n.t('customerSupport.issue_persists')}${
      hasCustomerSupportDetails ? ':' : '.'
    }`;
    return prefix;
  };
  const customerSupportPart = getCustomerSupportError({ getPrefixFunction });

  return (
    <>
      {openingSentence} {suffix} <br /> {customerSupportPart}
    </>
  );
};

export const getHostedPageSupportedPMTErrorMessage = () => {
  const openingSentence = i18n.t('reusable.no_supported_pmt_error_nessage');

  return getDefaultErrorMessage({ openingSentence });
};

export const isResetPINErrorUnexpected = (errorCode: string) =>
  errorCode !== ERROR_500_MESSAGES.ERROR_HOSTED_PAGE_PIN_NOT_VALID;
export const isOTPErrorUnexpected = (errorCode: string) =>
  ![
    ERROR_500_MESSAGES.ERROR_HOSTED_PAGE_INVALID_OTP_CODE,
    ERROR_500_MESSAGES.ERROR_HOSTED_PAGE_OTP_CODE_EXPIRED
  ].includes(errorCode);
